import { useMutation, useQueryClient } from "@tanstack/react-query";

import { GraphQLAPI, queries, GraphQLQuery } from "@examspark/api";
import {
  CreateResourceInput,
  IDSK,
  Resource,
  UpdateResourceInput,
} from "@examspark/types";

export const useMutationUpdatePlannerItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateResourceInput) => {
      const updateResult = await GraphQLAPI.query<
        GraphQLQuery<{ updateResource: Resource }>
      >({
        query: queries.resource.updateResource(),
        variables: {
          input,
        },
      });

      if (updateResult.errors) {
        throw new Error(updateResult.errors.map((e) => e.message).join("\n\n"));
      }

      queryClient.setQueriesData(
        ["planner-items"],
        (old: Resource[] | undefined) => {
          if (!old) return old;
          const updated = old.map((r) => {
            if (r.id === input.id && r.sk === input.sk) {
              // There is some sort of type mismatch between UpdateResourceInput and Resource
              // and this gets around it by filtering the fields we are allowed to update.
              // If you need to update any other fields using this mutation, feel free to add them here.
              const fieldsWeAreOkSetting = {
                description: input.description,
              };
              return {
                ...r,
                ...fieldsWeAreOkSetting,
              };
            }
            return r;
          });

          return updated;
        },
      );
      queryClient.invalidateQueries(["resources"]);
    },
  });
};

export const useMutationCreatePlannerItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateResourceInput) => {
      const createResult = await GraphQLAPI.query<
        GraphQLQuery<{ createResource: Resource }>
      >({
        query: queries.resource.createResource(),
        variables: {
          input,
        },
      });
      if (createResult.errors) {
        throw new Error(createResult.errors.map((e) => e.message).join("\n\n"));
      }

      // we can't use an optimistic mutation here because we don't have the
      // actual resource, just the ID
      queryClient.invalidateQueries(["planner-items"]);
      queryClient.invalidateQueries(["resources"]);

      return createResult;
    },
  });
};

export const useMutationDeletePlannerItem = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: IDSK & {
        workspaceId: string;
      },
    ) => {
      const deleteResult = await GraphQLAPI.query<
        GraphQLQuery<{
          deleteResource: Resource;
        }>
      >({
        query: queries.resource.deleteResource(),
        variables: {
          input,
        },
      });

      if (deleteResult.errors) {
        throw new Error(deleteResult.errors.map((e) => e.message).join("\n\n"));
      }

      queryClient.setQueriesData(
        ["planner-items"],
        (old: Resource[] | undefined) => {
          if (!old) return old;
          if (!deleteResult.data?.deleteResource) return old;

          return old.filter(
            (r) =>
              !(
                r.id === deleteResult.data?.deleteResource.id &&
                r.sk === deleteResult.data?.deleteResource.sk
              ),
          );
        },
      );

      // NOTE: you probably also want to setQueriesData on the classroom planner items
      // yourself

      return deleteResult;
    },
  });
};
